import React, { useRef, useState, useEffect } from "react";
import GenericTable from "../../../Components/GenericTable";
import { Button, Input, Space, Dropdown, message, Select, Tag } from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { eventAPI } from "../../../utils/api";
import "./style.css";

const Event = () => {
  const searchInput = useRef(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Keep the current table page in the URL so navigating into an event and
  // back (or a page refresh) lands on the same page instead of resetting to 1.
  const currentPage = Number(searchParams.get("page")) || 1;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  // Fetch events from API
  const fetchEvents = async (search = "", status = "", category = "") => {
    try {
      setLoading(true);
      const response = await eventAPI.getEvents(search, status, category);

      const formattedEvents = response.results.data.map((event) => ({
        key: event.id,
        clientName: event.client_name,
        category: event.category,
        eventName: event.name,
        startDate: event.start_date,
        endDate: event.end_date,
        status: event.status,
        value: event.value,
        id: event.id,
        depositMet: event.deposit_met,
        balanceDueWarning: event.balance_due_warning,
      }));

      setEvents(formattedEvents);
    } catch (error) {
      messageApi.error(error.message || "Failed to fetch events");
      console.error("Fetch events error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  const handleStatusChange = async (newStatus, record) => {
    const formData = new FormData();
    formData.append("status", newStatus);
    try {
      const response = await eventAPI.updateEvent(record.id, formData);
      // Reflect the server's actual status, not the one we asked for — the
      // backend can refuse a change (e.g. the auto-promote rules), so an
      // optimistic update here could show a status the server didn't apply.
      const confirmedStatus = response?.data?.status ?? newStatus;
      setEvents((prev) =>
        prev.map((e) =>
          e.key === record.key ? { ...e, status: confirmedStatus } : e
        )
      );
      messageApi.success("Event status updated!");
    } catch (error) {
      messageApi.error(error.message || "Failed to update event status");
    }
  };

  const handleSearch = (confirm, selectedKeys) => {
    setSearchValue(selectedKeys?.[0] ?? "");
    confirm();
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(confirm, selectedKeys)}
          style={{ marginBottom: 8 }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(confirm, selectedKeys)}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const handleDelete = async (record) => {
    try {
      await eventAPI.deleteEvent(record.id);
      setEvents((prev) => prev.filter((e) => e.key !== record.key));
      messageApi.success(`${record.eventName} deleted successfully`);
    } catch (error) {
      messageApi.error(error.message || "Failed to delete event");
    }
  };

  const statusStyleMap = {
    complete: {
      background: "#E6F4EA",
      color: "#237804",
    },
    booked: {
      background: "#E6F0FF",
      color: "#1D39C4",
    },
    in_query: {
      background: "#FFF4E5",
      color: "#AD6800",
    },
    cancel: {
      background: "#FFF1F0",
      color: "#A8071A",
    },
  };

  const columns = [
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName",
      ...getColumnSearchProps("clientName"),
    },
    {
      title: "Event Category",
      dataIndex: "category",
      key: "category",
      ...getColumnSearchProps("category"),
    },
    {
      title: "Event Name",
      dataIndex: "eventName",
      key: "eventName",
      ...getColumnSearchProps("eventName"),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
    },
    {
      title: "Event Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Complete", value: "complete" },
        { text: "Booked", value: "booked" },
        { text: "In Query", value: "in_query" },
        { text: "Cancel", value: "cancel" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status, record) => (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Select
            value={status}
            bordered={false}
            style={{
              width: 130,
              borderRadius: 6,
              ...statusStyleMap[status],
            }}
            onChange={(value) => handleStatusChange(value, record)}
            options={[
              { value: "complete", label: "Complete" },
              { value: "booked", label: "Booked" },
              { value: "in_query", label: "In Query" },
              { value: "cancel", label: "Cancel" },
            ]}
          />
        </div>
      ),
    },
    {
      title: "Payment",
      key: "payment",
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          {!record.depositMet && record.status !== "cancel" && (
            <Tag color="warning">Deposit Pending</Tag>
          )}
          {record.balanceDueWarning && <Tag color="error">Balance Due Soon</Tag>}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const items = [
          {
            key: "edit",
            icon: <EditOutlined style={{ color: "#3980F2" }} />,
            label: "Edit",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              navigate("/add-new-event", {
                state: { eventId: record.id, returnPage: currentPage },
              });
            },
          },
          {
            key: "delete",
            icon: <DeleteOutlined style={{ color: "#D11A2A" }} />,
            label: "Delete",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              handleDelete(record);
            },
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <span
              onClick={(e) => e.stopPropagation()}
              style={{
                fontSize: 20,
                cursor: "pointer",
                transform: "rotate(90deg)",
                display: "inline-block",
              }}
            >
              <MoreOutlined />
            </span>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="event-list">
      {contextHolder}
      <div className="btn">
        <Button
          icon={<PlusOutlined />}
          onClick={() => navigate("/add-new-event")}
        >
          Add New
        </Button>
      </div>

      <GenericTable
        title="Event Details"
        columns={columns}
        dataSource={events}
        loading={loading}
        pagination={{
          pageSize: 10,
          current: currentPage,
          // replace, not push — otherwise Back has to walk through every
          // page you clicked through before it leaves the list at all.
          onChange: (page) =>
            setSearchParams({ page: String(page) }, { replace: true }),
        }}
        onRowClick={(record) => {
          navigate("/view-event-detail", { state: { eventId: record.id } });
        }}
      />
    </div>
  );
};

export default Event;
